// ------------------------------------------------------------------------ //
// TanStack Query hooks for Summary
// ------------------------------------------------------------------------ //
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@shared/api/api';

// ------------------------------------------------------------------------ //
// Query Keys
// ------------------------------------------------------------------------ //
export const summaryKeys = {
  all: ['summary'],
  processed: (from, to, userId) => [...summaryKeys.all, 'processed', from, to, userId],
  direct: (from, to, userId) => [...summaryKeys.all, 'direct', from, to, userId],
  stats: (userId, date, activityTypeId, timezoneId) => [...summaryKeys.all, 'stats', userId, date, activityTypeId, timezoneId],
};

// ------------------------------------------------------------------------ //
// Get Summary Processed Inquiries
// ------------------------------------------------------------------------ //
export function useSummaryProcessedInquiries(from, to, userId) {
  return useQuery({
    queryKey: summaryKeys.processed(from, to, userId),
    queryFn: async () => {
      const response = await api.getSummaryProcessedInquiries({
        from,
        to,
        userId,
      });

      // Filter out inquiries with status: Échec, Abandon, or Annulation du RDV
      // Only keep inquiries with status "Succès"
      const filteredData = response.data.filter((inquiry) => {
        if (!inquiry.status || !inquiry.status.name) {
          // If no status info, keep it (fallback for backward compatibility)
          return true;
        }
        // Exclude: Échec, Abandon, Annulation du RDV
        const excludedStatuses = ['Échec', 'Echec', 'Abandon', 'Annulation du RDV'];
        return !excludedStatuses.includes(inquiry.status.name);
      });

      return filteredData;
    },
    enabled: !!from && !!userId,
    staleTime: 60000, // Summary data stays fresh for 1 minute
  });
}

// ------------------------------------------------------------------------ //
// Get Summary Direct Inquiries
// ------------------------------------------------------------------------ //
export function useSummaryDirectInquiries(from, to, userId) {
  return useQuery({
    queryKey: summaryKeys.direct(from, to, userId),
    queryFn: async () => {
      const response = await api.getSummaryDirectInquiries({
        from,
        to,
        userId,
      });
      return response.data;
    },
    enabled: !!from && !!userId,
    staleTime: 60000,
  });
}

// ------------------------------------------------------------------------ //
// Get Stats User
// ------------------------------------------------------------------------ //
export function useStatsUser(userId, date, activityTypeId = '', timezoneId = '') {
  return useQuery({
    queryKey: summaryKeys.stats(userId, date, activityTypeId, timezoneId),
    queryFn: async () => {
      const params = {
        userId,
        date,
      };

      if (activityTypeId !== '') {
        params.activityTypeId = activityTypeId;
      }

      if (timezoneId !== '') {
        params.timezoneId = timezoneId;
      }

      const response = await api.getStatsUser(params);

      if (!response) {
        throw new Error('Failed to fetch stats');
      }

      // Transform duration data (seconds to 00H00min)
      const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        return `${String(hours).padStart(2, '0')}H${String(minutes).padStart(2, '0')}min`;
      };

      return {
        ...response,
        durationDay: formatDuration(response.daily_activities_duration),
        durationMonth: formatDuration(response.monthly_activities_duration),
        durationMonthWeeks: formatDuration(response.weekdays_activities_duration),
        durationMonthWeekends: formatDuration(response.weekends_activities_duration),
      };
    },
    enabled: !!userId && !!date,
    staleTime: 60000, // Stats data stays fresh for 1 minute
  });
}

// ------------------------------------------------------------------------ //
// Create Adjustment Request Mutation
// ------------------------------------------------------------------------ //
export function useCreateAdjustmentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const response = await api.postInquiry({
        typeId: params.typeId,
        statusId: params.statusId,
        adjustNote: params.adjustNote,
        invoicesWeight: params.invoicesWeight,
        teacherId: params.teacherId,
        switchmanId: params.switchmanId,
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate summary queries to refetch the data
      queryClient.invalidateQueries({ queryKey: summaryKeys.all });
    },
  });
}
